// Geometry + distance helpers for the Command Center map.
// All distances in metres, bearings in degrees (0 = N, clockwise).

import type { Geometry, GeoShape, LatLon } from '../types/domain';

const EARTH_R = 6371000; // metres
const DEG = Math.PI / 180;

export function haversineMeters(a: LatLon, b: LatLon): number {
  const dLat = (b.lat - a.lat) * DEG;
  const dLon = (b.lon - a.lon) * DEG;
  const lat1 = a.lat * DEG;
  const lat2 = b.lat * DEG;
  const h =
    Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLon / 2) ** 2;
  return 2 * EARTH_R * Math.asin(Math.min(1, Math.sqrt(h)));
}

export function bearingDeg(from: LatLon, to: LatLon): number {
  const y = Math.sin((to.lon - from.lon) * DEG) * Math.cos(to.lat * DEG);
  const x =
    Math.cos(from.lat * DEG) * Math.sin(to.lat * DEG) -
    Math.sin(from.lat * DEG) * Math.cos(to.lat * DEG) * Math.cos((to.lon - from.lon) * DEG);
  return (Math.atan2(y, x) / DEG + 360) % 360;
}

const COMPASS = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'];
export function compassPoint(deg: number): string {
  return COMPASS[Math.round(((deg % 360) / 45)) % 8];
}

export function formatBearing(deg: number): string {
  return `${Math.round(deg).toString().padStart(3, '0')}° ${compassPoint(deg)}`;
}

export function formatDistance(m: number): string {
  if (!isFinite(m)) return '—';
  if (m >= 1000) return `${(m / 1000).toFixed(m >= 10000 ? 0 : 2)} km`;
  return `${Math.round(m)} m`;
}

// Nearest point on segment AB to P, using a local equirectangular projection
// (accurate for the short spans of an exercise AOR).
export function nearestPointOnSegment(p: LatLon, a: LatLon, b: LatLon): LatLon {
  const lat0 = (a.lat + b.lat) / 2 * DEG;
  const toXY = (q: LatLon) => ({ x: q.lon * DEG * Math.cos(lat0), y: q.lat * DEG });
  const P = toXY(p);
  const A = toXY(a);
  const B = toXY(b);
  const abx = B.x - A.x;
  const aby = B.y - A.y;
  const len2 = abx * abx + aby * aby;
  let t = len2 === 0 ? 0 : ((P.x - A.x) * abx + (P.y - A.y) * aby) / len2;
  t = Math.max(0, Math.min(1, t));
  return { lat: a.lat + (b.lat - a.lat) * t, lon: a.lon + (b.lon - a.lon) * t };
}

function point2(g: Geometry): LatLon | null {
  if (g.centerLat != null && g.centerLon != null) return { lat: g.centerLat, lon: g.centerLon };
  return null;
}

// Distance from a point to a geometry (0 if inside a BOX or within a POINT radius).
export function distanceToGeometry(p: LatLon, g: Geometry): number {
  if (g.shape === 'POINT') {
    const c = point2(g);
    if (!c) return Infinity;
    const d = haversineMeters(p, c);
    return Math.max(0, d - (g.radiusMeters ?? 0));
  }
  const coords = g.coords ?? [];
  if (g.shape === 'LINE' && coords.length >= 2) {
    const np = nearestPointOnSegment(p, coords[0], coords[1]);
    return haversineMeters(p, np);
  }
  if (g.shape === 'BOX' && coords.length >= 3) {
    if (pointInPolygon(p, coords)) return 0;
    let min = Infinity;
    for (let i = 0; i < coords.length; i++) {
      const a = coords[i];
      const b = coords[(i + 1) % coords.length];
      min = Math.min(min, haversineMeters(p, nearestPointOnSegment(p, a, b)));
    }
    return min;
  }
  const c = point2(g);
  return c ? haversineMeters(p, c) : Infinity;
}

export function pointInPolygon(p: LatLon, poly: LatLon[]): boolean {
  let inside = false;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const xi = poly[i].lon;
    const yi = poly[i].lat;
    const xj = poly[j].lon;
    const yj = poly[j].lat;
    const intersect =
      yi > p.lat !== yj > p.lat &&
      p.lon < ((xj - xi) * (p.lat - yi)) / (yj - yi) + xi;
    if (intersect) inside = !inside;
  }
  return inside;
}

// Every anchor point of a geometry (for bounds fitting): POINT → its centre,
// LINE/BOX → all vertices. Empty when the geometry has nothing placed.
export function geometryPoints(g: Geometry | null | undefined): LatLon[] {
  if (!g) return [];
  if (g.shape === 'POINT') {
    const c = point2(g);
    return c ? [c] : [];
  }
  const coords = g.coords ?? [];
  if (coords.length > 0) return coords;
  const c = point2(g);
  return c ? [c] : [];
}

// A representative point for a geometry (centre / midpoint / polygon centroid).
export function geometryCentroid(g: Geometry): LatLon | null {
  if (g.shape === 'POINT') return point2(g);
  const coords = g.coords ?? [];
  if (coords.length === 0) return point2(g);
  const sum = coords.reduce((acc, c) => ({ lat: acc.lat + c.lat, lon: acc.lon + c.lon }), { lat: 0, lon: 0 });
  return { lat: sum.lat / coords.length, lon: sum.lon / coords.length };
}

const WALK_MPS = 1.4; // fallback walking speed if no recent GPS speed

// Rough ETA in minutes given straight-line distance and an optional current speed.
export function etaMinutes(distanceM: number, speedMps?: number | null): number {
  const v = speedMps && speedMps > 0.3 ? speedMps : WALK_MPS;
  return distanceM / v / 60;
}

export function formatEta(distanceM: number, speedMps?: number | null): string {
  const mins = etaMinutes(distanceM, speedMps);
  if (!isFinite(mins)) return '—';
  if (mins < 1) return '<1 min';
  if (mins < 60) return `${Math.round(mins)} min`;
  const h = Math.floor(mins / 60);
  return `${h}h ${Math.round(mins % 60)}m`;
}

// Defensive adapter: normalize whatever geometry shape A returns into our Geometry.
// The contract (docs/06 §15/§16/§17) nests geometry under a `geometry` key on the
// Target/Farmbase DTOs (`{ shape, centerLat?, centerLon?, radiusMeters?, coords:[{lat,lon}] }`,
// or `null` when not yet drawn). We accept either the wrapper or a bare geometry object,
// tolerate legacy `polygonCoords`/`lng`, and infer `shape` when the backend omits it.
export function normalizeGeometry(input: any): Geometry | null {
  if (!input) return null;
  // Unwrap the nested `geometry` object when handed a whole DTO. When the key is
  // present but null (seed ships geometry empty), there is nothing to draw.
  const raw = 'geometry' in input ? input.geometry : input;
  if (!raw) return null;

  const centerLat = raw.centerLat ?? raw.lat ?? null;
  const centerLon = raw.centerLon ?? raw.centerLng ?? raw.lon ?? raw.lng ?? null;
  const radiusMeters = raw.radiusMeters ?? raw.radius ?? null;

  let coords: LatLon[] | null = null;
  if (Array.isArray(raw.coords)) {
    coords = raw.coords
      .map((c: any) =>
        Array.isArray(c)
          ? { lat: Number(c[0]), lon: Number(c[1]) }
          : { lat: Number(c.lat), lon: Number(c.lon ?? c.lng) }
      )
      .filter((c: LatLon) => isFinite(c.lat) && isFinite(c.lon));
  } else if (Array.isArray(raw.polygonCoords)) {
    coords = raw.polygonCoords
      .map((c: any) =>
        Array.isArray(c)
          ? { lat: Number(c[0]), lon: Number(c[1]) }
          : { lat: Number(c.lat), lon: Number(c.lon ?? c.lng) }
      )
      .filter((c: LatLon) => isFinite(c.lat) && isFinite(c.lon));
  }

  let shape: GeoShape | undefined = raw.shape;
  if (!shape) {
    if (coords && coords.length >= 4) shape = 'BOX';
    else if (coords && coords.length === 2) shape = 'LINE';
    else if (centerLat != null && centerLon != null) shape = 'POINT';
  }
  if (!shape) return null;

  // Preserve optional per-object styling (§15/§16) so map layers can honour it.
  const style = raw.style && typeof raw.style === 'object' ? (raw.style as Geometry['style']) : undefined;

  return { shape, centerLat, centerLon, radiusMeters, coords, style };
}
